import styled, { css } from 'styled-components';
import { applyOpacity } from '../../utils/color.utils';
// import 'react-datepicker/dist/react-datepicker.css';
import { HBDatepickerCustomTheme } from './HBDatepicker.types';
import { Z_INDEX } from '../../styles/variables';
import { transition } from '../../styles/animations';
import { vars } from '../../theme';

/////////////////////////////////////////////
// Utility functions
/////////////////////////////////////////////

const themeValue =
    (value: 'color' | 'accent_color', fallback: string) =>
    ({ customTheme }: { customTheme?: HBDatepickerCustomTheme }) =>
        customTheme?.[value] || fallback;

function todayBGColor({
    customTheme
}: {
    customTheme?: HBDatepickerCustomTheme;
}) {
    return customTheme?.accent_color
        ? applyOpacity(customTheme.accent_color, 9)
        : vars.colors.surface.accent;
}

function selectedDayStyles({
    customTheme
}: {
    customTheme?: HBDatepickerCustomTheme;
}) {
    const bgColor = customTheme?.accent_color || vars.colors.base.accent;
    const color = customTheme?.color || vars.colors.text.invertedPrimary;

    return css`
        background-color: ${bgColor};
        color: ${color};

        /* don't change color on hover or focus */
        &:hover,
        &:focus {
            background-color: ${bgColor};
        }
    `;
}

function hoverBGColor({
    customTheme
}: {
    customTheme?: HBDatepickerCustomTheme;
}) {
    return customTheme?.accent_color
        ? applyOpacity(customTheme.accent_color, 3)
        : vars.colors.surface.itemHover;
}

export const DatePickerStyled = styled.div<{
    customTheme?: HBDatepickerCustomTheme;
    hasError?: boolean;
}>`
    position: relative;
    width: 100%;

    &:hover svg path,
    &:focus-within svg path {
        fill: ${themeValue('accent_color', vars.colors.text.primary)};
    }

    // the wrapper over the entire datepicker (including input)
    .react-datepicker-wrapper {
        width: 100%;
    }

    /* The datepicker popup continer */
    .react-datepicker {
        font-family: ${vars.fonts.body};
        border-radius: ${vars.radius.md};
        box-shadow: ${vars.shadow['40']};
        background-color: ${vars.colors.surface.container};
        border: none;
        padding: 24px;
    }

    .react-datepicker__monthPicker {
        height: 170px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    /* Visually-hidden aria-live region for date announcements (new in v9, library CSS hides it) */
    .react-datepicker__aria-live {
        position: absolute;
        clip-path: circle(0);
        border: 0;
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        width: 1px;
        white-space: nowrap;
    }

    /* Visually-hidden text for screen readers (e.g. full weekday names in day-name cells) */
    .react-datepicker__sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
        border: 0;
    }

    .react-datepicker__header {
        background: ${vars.colors.surface.container};
        border: none;
    }

    /* Name of day in header (Sun, Mon, etc...) */
    .react-datepicker__day-name,
    .react-datepicker__time-name,
    .react-datepicker__day,
    /* react-datepicker nests the styles for __month-text under react-datepicker__month which
       causes the styles from the react-datepicker.css file to override these. For some reason
       though this happens only in the angular app :-/ So I prefixed all styles for month-text
       with .react-datepicker__month so to increase specificity */
    .react-datepicker__month .react-datepicker__month-text {
        font-size: 12px;
        line-height: 1;
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: ${vars.colors.text.primary};
        font-weight: 600;
        ${transition('all')}
    }

    .react-datepicker__day-name {
        height: 20px;
    }

    /* __day - (7), __month-text - (Jan) */
    .react-datepicker__day,
    .react-datepicker__month .react-datepicker__month-text,
    .react-datepicker__year .react-datepicker__year-text {
        font-size: 14px;
        font-weight: 400;
        cursor: pointer;

        &--disabled {
            color: ${vars.colors.text.disabled};
            pointer-events: none;
        }
    }

    .react-datepicker__month .react-datepicker__month--disabled {
        color: ${vars.colors.text.disabled};
        pointer-events: none;
    }

    .react-datepicker__day {
        border-radius: ${vars.radius.full};
    }

    .react-datepicker__month .react-datepicker__month-text,
    .react-datepicker__year .react-datepicker__year-text {
        height: 30px;
        width: 45px;
        border-radius: ${vars.radius.full};
    }

    .react-datepicker__year .react-datepicker__year-text {
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &:focus {
            outline: none;
            background-color: ${hoverBGColor};
        }
    }

    .react-datepicker__current-month,
    .react-datepicker-time__header,
    .react-datepicker-year-header {
        font-weight: 400;
        color: ${vars.colors.text.primary};
        margin-bottom: 10px;
    }

    /* day styles */

    .react-datepicker__day:hover,
    .react-datepicker__month .react-datepicker__month-text:hover,
    .react-datepicker__quarter-text:hover,
    .react-datepicker__year .react-datepicker__year-text:hover,
    .react-datepicker__day--keyboard-selected,
    .react-datepicker__month .react-datepicker__month-text--keyboard-selected,
    .react-datepicker__year .react-datepicker__year-text--keyboard-selected {
        background-color: ${hoverBGColor};
    }

    /* Year picker uses CSS :focus for keyboard navigation highlight; suppress the
       library's own --keyboard-selected highlight which would ghost on a different cell */
    .react-datepicker__year .react-datepicker__year-text--keyboard-selected {
        background-color: transparent;
    }

    /* today styles need to be before keyboard-selected styles as selected takes precedence over today */
    .react-datepicker__day--today,
    .react-datepicker__month .react-datepicker__month-text--today,
    .react-datepicker__year .react-datepicker__year-text--today {
        background-color: ${todayBGColor};
    }

    /* Issue: for some reason in the month picker, the --selected get's applied to the keyboard selected element too
       even though it's not the actual selected month yet. This is an issue with the library  */
    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range,
    .react-datepicker__month .react-datepicker__month--selected,
    .react-datepicker__month .react-datepicker__month-text--selected,
    .react-datepicker__month .react-datepicker__month-text--in-selecting-range,
    .react-datepicker__month .react-datepicker__month-text--in-range,
    .react-datepicker__year .react-datepicker__year-text--selected,
    .react-datepicker__year .react-datepicker__year-text--in-selecting-range,
    .react-datepicker__year .react-datepicker__year-text--in-range {
        ${selectedDayStyles};
    }

    .react-datepicker__day--outside-month {
        color: ${vars.colors.text.disabled};
    }

    .react-datepicker__triangle {
        display: none;
    }

    .react-datepicker-popper {
        z-index: ${Z_INDEX.TOAST};
        &[data-placement^='bottom'] {
            margin-top: -10px;
        }
        &[data-placement^='top'] .react-datepicker {
            min-height: 321px;
        }
    }

    .react-datepicker__navigation {
        top: 20px;
    }

    .react-datepicker__navigation--previous {
        left: 20px;
    }

    .react-datepicker__navigation--next {
        right: 20px;
    }

    div[role='button'],
    button {
        outline: none;
    }

    input {
        width: 100%;
        ${({ hasError }) =>
            hasError &&
            css`
                background: ${vars.colors.surface.danger} !important;
                border: none !important;
            `}
    }

    /* Month picker */
    .react-datepicker__month-wrapper {
        width: 220px;
        display: flex;
        justify-content: space-between;
    }

    /* Year picker */
    .react-datepicker__year {
        margin: 0;
        width: 220px;
    }

    .react-datepicker__year-wrapper {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        column-gap: 36px;
        row-gap: 18px;
    }
`;

export const Calendar = styled.label`
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;

    i {
        display: flex;
    }
`;
